<template>
  <tr>
    <th>
      <div>
        <span
          v-if="isJudge"
          v-tooltip="'Judge'"
          class="tooltip-hint"
        >
          <Gavel />
        </span>
        <span
          v-if="!isJudge && !user.completedPayment"
          :class="{ 'line-through': !user.isActive }"
          class="has-text-grey"
        >{{ user.firstName }}&nbsp;{{ user.lastName }}</span>
        <span
          v-else
          :class="{ 'line-through': !user.isActive }"
        >{{ user.firstName }}&nbsp;{{ user.lastName }}</span>
      </div>
    </th>
    <td>
      <span
        v-if="!isJudge"
        class="tooltip-hint"
        v-tooltip="ageTooltipContent"
      >
        {{ age }}
        <span v-if="age < 18">
          <AccountChild />
        </span>
      </span>
      <span
        v-else
        class="has-text-grey-light"
      >N/A</span>
    </td>
    <td>
      <v-popover
        v-if="user.country"
      >
        <div
          class="tooltip-hint d-inline-block"
          @click="toggleTooltip"
        >
          <span :class="`flag-icon flag-icon-${user.country.code.toLowerCase() }`" />
          <span v-if="user.country.code === 'US'">
            {{ user.state.code }}
          </span>
        </div>

        <template slot="popover">
          <a
            v-close-popover
            class="close-popover"
          >×</a>

          <p><b>Address</b>: {{ user.address.street}}<br>
          <b>City</b>: {{ user.address.city}}<br>
          <b>State/Province/Region</b>: {{ user.state.name}} {{ user.state.code}}<br>
          <b>Zip Code</b>: {{ user.address.zipCode}}<br>
          <b>Country</b>: {{ user.country.name}} <span :class="countryFlagClass"></span></p>
        </template>
      </v-popover>
      <div v-else>
        <span class="has-text-grey-light">N/A</span>
      </div>
    </td>
    <td>
      <div
        v-if="user.divisions.length > 0"
      >
        <div v-if="!isJudge">
          <span
            v-for="(division, index) in user.divisions"
            :key="division._id"
          >

            <span
              v-if="division.hasSubmitted && division.scores.length > 0"
            >
              <Gavel class="has-text-success" />
            </span>
            <span
              v-else-if="division.hasSubmitted && division.scores.length === 0"
            >
              <Video class="has-text-success" />
            </span>
            <span
              v-else
            >
              <CheckboxBlankCircle class="has-text-warning" />
            </span>
            {{ division.name }}

            <span v-if="index < user.divisions.length - 1"><br></span>
          </span>
        </div>
        <div v-else>
          <JudgeDivisions
            :user-id="user._id"
            @changeDivisions="emitJudgeDivisionModal"
          />
        </div>
      </div>

      <div v-else>
        <span class="has-text-grey-light">
          <span v-if="!isJudge">0</span>
        <a
          v-if="isJudge"
          href="#"
          class="button is-small is-link"
          @click.prevent="emitJudgeDivisionModal"
        ><span><PlusCircle />&nbsp;Assign division</span></a>
        </span>
      </div>
    </td>
    <td>{{ user.email }}</td>
    <td>
      <span
        class="tooltip-hint"
        v-tooltip="formatDate(user.createDate, 'DD MMM YYYY  @ HH:mm')"
      >
        {{ formatDate(user.createDate, 'DD MMM YYYY') }}
      </span>
    </td>
    <td class="has-text-centered">
      <div
        v-if="user.role === 'participant'"
      >
        <Check
          v-if="user.completedPayment"
          class="has-text-success"
        />
        <Minus v-else />
      </div>
      <div v-else>
        <span class="has-text-grey-light">N/A</span>
      </div>
    </td>
    <td>
      <div class="dropdown is-hoverable is-right">
        <div class="dropdown-trigger">
          <button class="button is-light" aria-haspopup="true" aria-controls="dropdown-menu">
            <span>Actions</span>
            <span>&nbsp;<ChevronDown aria-hidden="true" /></span>
          </button>
        </div>
        <div class="dropdown-menu" id="dropdown-menu" role="menu">
          <div class="dropdown-content">
            <a
              v-if="user.isActive"
              href="#"
              class="dropdown-item has-text-danger"
              @click.prevent="disableUser"
            >
              <span>Deactivate account</span>
            </a>
            <a
              v-else
              href="#"
              class="dropdown-item has-text-success"
              @click.prevent="disableUser"
            >
              <span>Activate account</span>
            </a>
          </div>
        </div>
      </div>
    </td>
  </tr>
</template>

<script>
import JudgeDivisions from '@/components/admin/users/JudgeDivisions'
import Gavel from 'vue-material-design-icons/Gavel.vue'
import AccountChild from 'vue-material-design-icons/AccountChild.vue'
import Check from 'vue-material-design-icons/Check.vue'
import Minus from 'vue-material-design-icons/Minus.vue'
import Video from 'vue-material-design-icons/Video.vue'
import CheckboxBlankCircle from 'vue-material-design-icons/CheckboxBlankCircle.vue'
import PlusCircle from 'vue-material-design-icons/PlusCircle.vue'
import ChevronDown from 'vue-material-design-icons/ChevronDown.vue'
import dateMixins from '@/mixins/dates'

export default {
  name: 'UserRow',
  mixins: [dateMixins],
  components: {
    JudgeDivisions,
    Gavel,
    AccountChild,
    Check,
    Minus,
    Video,
    CheckboxBlankCircle,
    ChevronDown,
    PlusCircle
  },
  props: {
    user: {
      type: Object,
      default: () => {
        return {}
      }
    }
  },
  data: () => ({
    openTooltip: false
  }),
  computed: {
    countryFlagClass () {
      return `flag-icon flag-icon-${this.user.country.code.toLowerCase()}`
    },
    age () {
      return this.getUserAge(this.user.birthdate)
    },
    isJudge () {
      return this.user.role === 'judge'
    },
    countryTooltip () {
      let content = `<div>

        <p><b>Address</b>: ${this.user.address.street}<br>
            <b>City</b>: ${this.user.address.city}<br>
            <b>State/Province/Region</b>: ${this.user.state.name} ${this.user.state.code}<br>
            <b>Zip Code</b>: ${this.user.address.zipCode}<br>
            <b>Country</b>: ${this.user.country.name} <span class="flag-icon flag-icon-${this.user.country.code.toLowerCase()}"></span></p>
      </div>`
      return content
    },
    ageTooltipContent () {
      let content = this.formatDate(this.user.birthdate, 'DD MMM YYYY')
      if (this.user.role !== 'judge') {
        if (this.age < 18) {
          content = `<div>
            <p><b>Underage participant</b></p>
            <p>Birthdate: ${this.formatDate(this.user.birthdate, 'DD MMM YYYY')}</p>
            <hr>
            <p><b>Parent/Guardian</b></p>
            <p>Name: ${this.user.guardianInfo.firstName} ${this.user.guardianInfo.lastName}</p>
            <p>Email: ${this.user.guardianInfo.email}</p>
          </div>`
        }
      } else {
        content = 'N/A'
      }
      return content
    }
  },
  methods: {
    toggleTooltip () {
      this.openTooltip = !this.openTooltip
    },
    disableUser () {
      console.log('disableUser')
      let msg = this.user.isActive ? 'This user will no longer be able to login. Proceed?' : 'This user will be able to login. Proceed?'
      let confirm = this.user.isActive ? 'Deactivate' : 'Activate'

      this.$toasted.show(msg, {
        duration: null,
        action: [
          {
            text: 'Cancel',
            onClick: (e, toastObject) => {
              toastObject.goAway(0)
            }
          },
          {
            text: confirm,
            onClick: (e, toastObject) => {
              toastObject.goAway(0)
              this.dispatchDeactivate()
            }
          }
        ]
      })
    },
    dispatchDeactivate () {
      console.log('dispatchDeactivate')
      this.$store
        .dispatch('admin/UPDATE_USER', {
          userId: this.user._id,
          updatePipeline: {
            '$set': {
              isActive: !this.user.isActive
            }
          }
        })
        .then(() => {
          this.$toasted.success('Account state changed.')
        })
    },
    emitJudgeDivisionModal () {
      this.$emit('editDivisions', this.user._id)
    }
  }
}
</script>

<style lang="scss">
  .flag-icon{
    border-radius: 2px;
  }
  .close-popover {
    position: absolute;
    top: 3px;
    right: 12px;
    font-size: 1.3em;
  }
  .tooltip.popover .popover-inner {
    padding: 30px 24px 24px;
    border: 1px solid #DDD;
  }
</style>
